import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import {
    fetchMetaCampaignList,
    fetchMetaCampaignHistoryList,
    fetchMetaCampaignDetails,
    fetchMetaCampaignAnalytics,
    fetchMetaCampaignAudienceBreakdown,
} from "../../api/growthEngine/metaCampaign.api";
import { showToast } from "../../helper/ShowToast";
import {
    getMetaErrorCodeFromAxiosError,
    isMetaTokenErrorCode,
} from "../../helper/metaTokenErrors";

export const META_CAMPAIGN_LIST_ROOT = "meta-campaign-list";
export const META_CAMPAIGN_HISTORY_ROOT = "meta-campaign-history";
export const META_CAMPAIGN_DETAILS_ROOT = "meta-campaign-details";
export const META_CAMPAIGN_ANALYTICS_ROOT = "meta-campaign-analytics";
export const META_CAMPAIGN_AUDIENCE_ROOT = "meta-campaign-audience";

const VALID_META_AUDIENCE_RANGES = new Set(["all", "weekly", "monthly", "yearly"]);

/** Maps performance period (all/weekly/monthly/yearly) to Meta insights range. */
export const mapMetaCampaignBreakdownRange = (period) => {
    const normalized = String(period || "all").toLowerCase();
    if (VALID_META_AUDIENCE_RANGES.has(normalized)) return normalized;
    return "all";
};

export const metaCampaignListQueryKey = (userKey, page = 1, limit = 10) => [
    META_CAMPAIGN_LIST_ROOT,
    userKey,
    page,
    limit,
];

export const metaCampaignHistoryQueryKey = (
    userKey,
    { page = 1, limit = 10, interval = "", effectiveStatus = "" } = {}
) => [
    META_CAMPAIGN_HISTORY_ROOT,
    userKey,
    page,
    limit,
    interval || "",
    effectiveStatus || "",
];

export const metaCampaignDetailsQueryKey = (userKey, campaignId) => [
    META_CAMPAIGN_DETAILS_ROOT,
    userKey,
    campaignId,
];

export const metaCampaignAnalyticsQueryKey = (userKey, range = "all") => [
    META_CAMPAIGN_ANALYTICS_ROOT,
    userKey,
    range,
];

export const metaCampaignAudienceQueryKey = (userKey, range = "all") => [
    META_CAMPAIGN_AUDIENCE_ROOT,
    userKey,
    range,
];

export const invalidateMetaCampaignList = (queryClient) =>
    queryClient.invalidateQueries({ queryKey: [META_CAMPAIGN_LIST_ROOT] });

export const invalidateMetaCampaignHistory = (queryClient) =>
    queryClient.invalidateQueries({ queryKey: [META_CAMPAIGN_HISTORY_ROOT] });

export const invalidateMetaCampaignDetails = (queryClient) =>
    queryClient.invalidateQueries({ queryKey: [META_CAMPAIGN_DETAILS_ROOT] });

export const invalidateMetaCampaignAnalytics = (queryClient) =>
    queryClient.invalidateQueries({ queryKey: [META_CAMPAIGN_ANALYTICS_ROOT] });

export const invalidateMetaCampaignAudienceBreakdown = (queryClient) =>
    queryClient.invalidateQueries({ queryKey: [META_CAMPAIGN_AUDIENCE_ROOT] });

/** Call after create / update / duplicate so list, history, editor, and charts refetch. */
export const invalidateMetaCampaignCaches = (queryClient) => {
    if (!queryClient) return;
    invalidateMetaCampaignList(queryClient);
    invalidateMetaCampaignHistory(queryClient);
    invalidateMetaCampaignDetails(queryClient);
    invalidateMetaCampaignAnalytics(queryClient);
    invalidateMetaCampaignAudienceBreakdown(queryClient);
};

const FIFTEEN_MINUTES_MS = 15 * 60 * 1000;
const SIX_HOURS_MS = 6 * 60 * 60 * 1000;

const notifyMetaQueryError = (error, fallbackMessage) => {
    const code = getMetaErrorCodeFromAxiosError(error);
    // Banner handles reconnect UX — avoid duplicate error toasts for token issues
    if (isMetaTokenErrorCode(code)) return;
    showToast(
        "error",
        error?.response?.data?.message || fallbackMessage
    );
};

export const useMetaCampaignList = ({
    userKey,
    page = 1,
    limit = 10,
    enabled = true,
} = {}) =>
    useQuery({
        queryKey: metaCampaignListQueryKey(userKey, page, limit),
        queryFn: () => fetchMetaCampaignList({ page, limit }),
        enabled: enabled && !!userKey,
        staleTime: FIFTEEN_MINUTES_MS,
        refetchOnWindowFocus: false,
        retry: 1,
        onError: (error) => {
            notifyMetaQueryError(error, "Failed to load Meta campaigns");
        },
    });

export const useMetaCampaignHistoryList = ({
    userKey,
    page = 1,
    limit = 10,
    interval = "",
    effectiveStatus = "",
    enabled = true,
} = {}) =>
    useQuery({
        queryKey: metaCampaignHistoryQueryKey(userKey, {
            page,
            limit,
            interval,
            effectiveStatus,
        }),
        queryFn: ({ queryKey }) => {
            const [, , queryPage, queryLimit, queryInterval, queryStatus] = queryKey;
            return fetchMetaCampaignHistoryList({
                page: queryPage,
                limit: queryLimit,
                interval: queryInterval,
                effectiveStatus: queryStatus,
            });
        },
        enabled: enabled && !!userKey,
        staleTime: FIFTEEN_MINUTES_MS,
        placeholderData: keepPreviousData,
        refetchOnWindowFocus: false,
        retry: 1,
        onError: (error) => {
            notifyMetaQueryError(error, "Failed to load campaign history");
        },
    });

export const useMetaCampaignDetails = ({
    userKey,
    campaignId,
    enabled = true,
} = {}) =>
    useQuery({
        queryKey: metaCampaignDetailsQueryKey(userKey, campaignId),
        queryFn: () => fetchMetaCampaignDetails(campaignId),
        enabled: enabled && !!userKey && !!campaignId,
        staleTime: FIFTEEN_MINUTES_MS,
        refetchOnWindowFocus: false,
        retry: 1,
        onError: (error) => {
            notifyMetaQueryError(error, "Failed to load campaign details");
        },
    });

export const useMetaCampaignAnalytics = ({
    userKey,
    range = "all",
    enabled = true,
} = {}) => {
    const queryClient = useQueryClient();
    const normalizedRange = mapMetaCampaignBreakdownRange(range);
    const queryKey = metaCampaignAnalyticsQueryKey(userKey, normalizedRange);

    return useQuery({
        queryKey,
        queryFn: async () => {
            const state = queryClient.getQueryState(queryKey);
            const shouldRefresh = Boolean(state?.isInvalidated);

            return fetchMetaCampaignAnalytics({
                range: normalizedRange,
                refresh: shouldRefresh,
            });
        },
        enabled: enabled && !!userKey,
        staleTime: FIFTEEN_MINUTES_MS,
        placeholderData: keepPreviousData,
        refetchOnWindowFocus: false,
        retry: 1,
        onError: (error) => {
            notifyMetaQueryError(error, "Failed to load Meta campaign analytics");
        },
    });
};

export const useMetaCampaignAudienceBreakdown = ({
    userKey,
    range = "all",
    enabled = true,
} = {}) => {
    const queryClient = useQueryClient();
    const queryKey = metaCampaignAudienceQueryKey(userKey, range);

    return useQuery({
        queryKey,
        queryFn: async () => {
            const state = queryClient.getQueryState(queryKey);
            const shouldRefresh =
                !state?.dataUpdatedAt ||
                Date.now() - state.dataUpdatedAt >= SIX_HOURS_MS ||
                Boolean(state?.isInvalidated);

            return fetchMetaCampaignAudienceBreakdown({
                range,
                refresh: shouldRefresh,
            });
        },
        enabled: enabled && !!userKey,
        staleTime: SIX_HOURS_MS,
        refetchOnWindowFocus: false,
        retry: 1,
        onError: (error) => {
            notifyMetaQueryError(
                error,
                "Failed to load Meta campaign audience breakdown"
            );
        },
    });
};
